import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, requestJson } from './client';
import { getSessionToken } from './embeddedToken';

export const DUTY_PERIODS = [
  'Постоянно',
  'Ежедневно',
  'Еженедельно',
  'Ежемесячно',
  'Ежеквартально',
  'Ежегодно',
  'По графику',
  'По запросу',
] as const;

/** Куда писать по теме связи. Совпадает со справочником на сервере. */
export const CONTACT_CHANNELS = ['Чат Bitrix24', 'Задача в Bitrix24', 'Телефон', 'Почта', 'Лично'] as const;

/**
 * Связь: тема и как по ней общаться. direction «in» — с чем идут к сотруднику,
 * «out» — с чем он идёт к другим. person — кому писать («out») или кто отвечает вместо него («in»).
 */
export interface Contact {
  direction: 'in' | 'out';
  topic: string;
  channel: string;
  person?: string;
  /** Закреплённый сотрудник: ФИО для показа берётся из его карточки, по клику открывается она же. */
  personId?: string;
  reply?: string;
}

/** Что сотрудник делает по показателю KPI. Описание, а не факт: числа — в «Оклад и KPI». */
export interface KpiCommitment {
  name: string;
  what: string;
  measure?: string;
  /** Вес в премии, проценты. */
  weight?: number;
  source?: string;
}

export interface RegulationFile {
  id: string;
  name: string;
  contentType: string;
  sizeBytes: number;
}

export interface Duty {
  title: string;
  period: string;
  /** Рабочая область: программа, участок или направление. */
  area?: string;
}

export interface Regulation {
  id: string;
  scope: 'group' | 'employee';
  versionId: string;
  version: number;
  updatedAt: string;
  updatedBy: string;
  rules: string[];
  duties: Duty[];
  /** Необязательны: версии регламента до появления связей их не содержат. */
  contacts?: Contact[];
  kpiCommitments?: KpiCommitment[];
  files: RegulationFile[];
}

export interface EmployeeRegulation {
  group: { id: string; name: string; source: 'auto' | 'manual' } | null;
  regulation: Regulation | null;
  personal: boolean;
  signedAt: string | null;
  canSign: boolean;
  canEdit: { group: boolean; personal: boolean };
  canChangeGroup: boolean;
}

export interface RegulationDraft {
  rules: string[];
  duties: Duty[];
  contacts: Contact[];
  kpiCommitments: KpiCommitment[];
  files: RegulationFile[];
}

export interface RoleGroupSummary {
  id: string;
  key: string;
  name: string;
  members: number;
  signed: number;
  regulation: { version: number; updatedAt: string } | null;
}

export interface RoleGroupMember {
  id: string;
  fullName: string;
  position: string | null;
  photoUrl: string | null;
  source: 'auto' | 'manual';
  personal: boolean;
  hasRegulation: boolean;
  signedAt: string | null;
}

export const regulationKey = (employeeId: string | null) => ['employee', employeeId, 'regulation'] as const;

export function useEmployeeRegulation(employeeId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: regulationKey(employeeId),
    queryFn: () => requestJson<EmployeeRegulation>(`/api/v1/employees/${employeeId}/regulation`),
    enabled: Boolean(employeeId) && enabled,
  });
}

const draftBody = (draft: RegulationDraft) => ({
  rules: draft.rules,
  duties: draft.duties,
  contacts: draft.contacts,
  kpiCommitments: draft.kpiCommitments,
  fileIds: draft.files.map((file) => file.id),
});

export function useRegulationActions(employeeId: string) {
  const queryClient = useQueryClient();
  const update = (data: EmployeeRegulation) => {
    queryClient.setQueryData(regulationKey(employeeId), data);
    void queryClient.invalidateQueries({ queryKey: ['role-groups'] });
  };
  const sign = useMutation({
    mutationFn: (versionId: string) =>
      requestJson<EmployeeRegulation>(`/api/v1/employees/${employeeId}/regulation/sign`, {
        method: 'POST',
        body: JSON.stringify({ versionId }),
      }),
    onSuccess: update,
  });
  const save = useMutation({
    mutationFn: (input: { scope: 'group' | 'personal'; draft: RegulationDraft }) =>
      requestJson<EmployeeRegulation>(`/api/v1/employees/${employeeId}/regulation`, {
        method: 'PUT',
        body: JSON.stringify({ scope: input.scope, ...draftBody(input.draft) }),
      }),
    onSuccess: update,
  });
  const changeGroup = useMutation({
    mutationFn: (roleGroupId: string) =>
      requestJson<EmployeeRegulation>(`/api/v1/employees/${employeeId}/role-group`, {
        method: 'PUT',
        body: JSON.stringify({ roleGroupId }),
      }),
    onSuccess: update,
  });
  return { sign, save, changeGroup };
}

export function useRoleGroups(enabled = true) {
  return useQuery({
    queryKey: ['role-groups'],
    queryFn: () => requestJson<{ items: RoleGroupSummary[]; canEdit: boolean }>('/api/v1/role-groups'),
    enabled,
  });
}

export function useRoleGroup(id: string | undefined) {
  return useQuery({
    queryKey: ['role-groups', id],
    queryFn: () =>
      requestJson<{
        id: string;
        name: string;
        members: RoleGroupMember[];
        regulation: Regulation | null;
        canEdit: boolean;
      }>(`/api/v1/role-groups/${id}`),
    enabled: Boolean(id),
  });
}

export function useSaveGroupRegulation(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draft: RegulationDraft) =>
      requestJson<Regulation>(`/api/v1/role-groups/${id}/regulation`, {
        method: 'PUT',
        body: JSON.stringify(draftBody(draft)),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['role-groups'] }),
  });
}

export function authHeaders(): Record<string, string> {
  const token = getSessionToken();
  return token ? { authorization: `Bearer ${token}` } : {};
}

/** Загрузка файла регламента: тело — сам файл, имя — в заголовке. */
export async function uploadFile(file: File): Promise<RegulationFile> {
  const response = await fetch('/api/v1/files', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/octet-stream',
      'x-file-name': encodeURIComponent(file.name),
      'x-goodway-csrf': '1',
      ...authHeaders(),
    },
    body: file,
  });
  const body = (await response.json().catch(() => null)) as
    | (RegulationFile & { error?: { message?: string; code?: string } })
    | null;
  if (!response.ok) {
    throw new ApiError(response.status, 'BAD_REQUEST', body?.error?.message ?? 'Файл не загружен');
  }
  return body as RegulationFile;
}

/**
 * Открыть файл. Во встроенном режиме Bitrix24 cookie может быть заблокирована, поэтому файл
 * запрашивается с токеном и отдаётся браузеру как временная ссылка.
 */
export async function openFile(file: RegulationFile): Promise<void> {
  const response = await fetch(`/api/v1/files/${file.id}`, {
    credentials: 'same-origin',
    headers: authHeaders(),
  });
  if (!response.ok) throw new ApiError(response.status, 'FORBIDDEN', 'Файл недоступен');
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  if (file.contentType === 'application/pdf') link.target = '_blank';
  else link.download = file.name;
  link.rel = 'noopener';
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
